import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type PaulLessonStatus = 'captured' | 'accepted' | 'rejected' | 'retired';
export type PaulVerificationStatus = 'unverified' | 'pending' | 'verified' | 'rejected';
export type PaulConfidence = 'low' | 'medium' | 'high';
export type PaulLessonLifecycleStatus = 'open' | 'accepted' | 'training_ready' | 'needs_review' | 'resolved' | 'superseded' | 'duplicate' | 'rejected' | 'retired';

export interface PaulTraceSummary {
  tool_count?: number;
  tools?: string[];
  statuses?: string[];
  warnings?: string[];
}

export interface PaulCoachRun {
  coach_run_uuid: string;
  conversation_uuid: string | null;
  domain_uuid: string;
  user_uuid: string;
  username: string | null;
  prompt: string;
  answer: string | null;
  response_status: string | null;
  model: string | null;
  latency_ms: number | null;
  trace_summary: PaulTraceSummary;
  correction_status: 'open' | 'coached' | 'dismissed';
  source: 'admin_chat' | 'evaluation';
  evaluation_suite: string | null;
  evaluation_case_id: string | null;
  evaluation_category: string | null;
  evaluation_status: 'passed' | 'failed' | 'error' | null;
  lesson_count: number | null;
  verified_lesson_count: number | null;
  training_ready_count: number | null;
  created_at: string;
  updated_at: string;
  request_payload?: Record<string, unknown>;
  response_payload?: Record<string, unknown>;
  tool_trace?: unknown[];
}

export interface PaulCoachLesson {
  lesson_uuid: string;
  coach_run_uuid: string;
  domain_uuid: string;
  user_uuid: string;
  username: string | null;
  lesson_type: string;
  severity: 'low' | 'medium' | 'high';
  title: string;
  correction: string | null;
  expected_behavior: string | null;
  expected_answer: string | null;
  status: PaulLessonStatus;
  tags: string[];
  created_at: string;
  updated_at: string | null;
  coach_confidence: PaulConfidence | null;
  source_of_truth: string | null;
  evidence_payload: unknown;
  technical_diagnosis: string | null;
  business_notes: string | null;
  verification_status: PaulVerificationStatus;
  verification: Record<string, unknown>;
  training_eligible: boolean;
  lifecycle_status: PaulLessonLifecycleStatus;
  resolution_code: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  resolution_run_uuid: string | null;
  canonical_lesson_uuid: string | null;
  lifecycle: Record<string, unknown>;
  payload: Record<string, unknown>;
}

export interface PaulCoachAskResponse {
  ok: boolean;
  coach_run_uuid: string;
  conversation_uuid: string;
  conversation_turn_count: number;
  context_turns: number;
  prompt: string;
  answer: string | null;
  chat_result: Record<string, any>;
  trace_summary: PaulTraceSummary;
  latency_ms: number;
  lesson_options?: { lesson_types?: string[] };
  message?: string;
}

export interface PaulConversation {
  conversation_uuid: string;
  domain_uuid: string;
  user_uuid: string;
  username: string | null;
  title: string;
  status: 'active' | 'archived';
  turn_count: number;
  last_message_at: string;
  created_at: string;
  updated_at: string;
}

export interface PaulConversationRun {
  coach_run_uuid: string;
  conversation_uuid: string;
  prompt: string;
  answer: string | null;
  response_status: string | null;
  model: string | null;
  latency_ms: number | null;
  trace_summary: PaulTraceSummary;
  created_at: string;
}

export interface PaulConversationResponse {
  ok: boolean;
  conversation: PaulConversation;
  runs: PaulConversationRun[];
  meta: { limit: number; truncated: boolean };
  message?: string;
}

export interface PaulRunListResponse {
  ok: boolean;
  runs: PaulCoachRun[];
  meta: { total: number; limit: number; offset: number; source?: string };
}

export interface PaulRunDetailResponse {
  ok: boolean;
  run: PaulCoachRun;
  lessons: PaulCoachLesson[];
}

export interface PaulLessonListResponse {
  ok: boolean;
  lessons: PaulCoachLesson[];
  meta: { total: number; limit: number; offset: number };
}

export interface PaulFeedbackRequest {
  coach_run_uuid: string;
  lesson_type: string;
  severity: 'low' | 'medium' | 'high';
  title?: string;
  correction?: string;
  expected_behavior?: string;
  expected_answer?: string;
  coach_confidence?: PaulConfidence;
  source_of_truth?: string;
  evidence_payload?: { note?: string };
  technical_diagnosis?: string;
  business_notes?: string;
  verification_status?: PaulVerificationStatus;
  tags?: string[];
}

export interface PaulLessonUpdateRequest {
  status?: PaulLessonStatus;
  verification_status?: PaulVerificationStatus;
  title?: string;
  correction?: string;
  expected_behavior?: string;
  expected_answer?: string;
  coach_confidence?: PaulConfidence;
  source_of_truth?: string;
  evidence_payload?: { note?: string };
  technical_diagnosis?: string;
  business_notes?: string;
  resolution_note?: string;
}

@Injectable({ providedIn: 'root' })
export class PaulAdminApiService {
  constructor(private readonly http: HttpClient) {}

  ask(message: string, conversationUuid?: string): Observable<PaulCoachAskResponse> {
    return this.http.post<PaulCoachAskResponse>('/api/paul/ask.php', {
      message,
      ...(conversationUuid ? { conversation_uuid: conversationUuid } : {}),
    });
  }

  getConversation(conversationUuid: string): Observable<PaulConversationResponse> {
    return this.http.get<PaulConversationResponse>('/api/paul/conversation.php', {
      params: new HttpParams().set('conversation_uuid', conversationUuid),
    });
  }

  submitFeedback(request: PaulFeedbackRequest): Observable<{ ok: boolean; lesson_uuid: string; lesson: PaulCoachLesson }> {
    return this.http.post<{ ok: boolean; lesson_uuid: string; lesson: PaulCoachLesson }>('/api/paul/feedback.php', request);
  }

  listRuns(query: {
    limit?: number;
    offset?: number;
    q?: string;
    response_status?: string;
    correction_status?: string;
    model?: string;
    source?: 'admin_chat' | 'evaluation';
    evaluation_status?: 'passed' | 'failed' | 'error';
  }): Observable<PaulRunListResponse> {
    return this.http.get<PaulRunListResponse>('/api/paul/runs.php', { params: this.params(query) });
  }

  getRun(coachRunUuid: string): Observable<PaulRunDetailResponse> {
    return this.http.get<PaulRunDetailResponse>('/api/paul/run.php', {
      params: new HttpParams().set('coach_run_uuid', coachRunUuid),
    });
  }

  listLessons(query: {
    limit?: number;
    offset?: number;
    q?: string;
    status?: PaulLessonStatus;
    lesson_type?: string;
    severity?: string;
    verification_status?: PaulVerificationStatus;
  }): Observable<PaulLessonListResponse> {
    return this.http.get<PaulLessonListResponse>('/api/paul/lessons.php', { params: this.params(query) });
  }

  updateLesson(lessonUuid: string, request: PaulLessonUpdateRequest): Observable<{ ok: boolean; lesson: PaulCoachLesson }> {
    return this.http.put<{ ok: boolean; lesson: PaulCoachLesson }>('/api/paul/lesson.php', request, {
      params: new HttpParams().set('lesson_uuid', lessonUuid),
    });
  }

  private params(values: Record<string, string | number | undefined>): HttpParams {
    let params = new HttpParams();
    for (const [name, value] of Object.entries(values)) {
      if (value !== undefined && value !== '') {
        params = params.set(name, String(value));
      }
    }

    return params;
  }
}
